import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from './ui/button';
import {
  X, ArrowLeft, ArrowRight, Loader2, CheckCircle,
  Send, Copy, Check, Zap, TrendingUp, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { TELEGRAM_CTA_URL, getTelegramDeepLink } from '../lib/telegram';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRATEGY_COMMANDS = {
  scalper: { cmd: '/scalper', icon: Zap, tf: '1m - 5m', color: 'text-yellow-400' },
  intraday: { cmd: '/intraday', icon: TrendingUp, tf: '5m - 15m', color: 'text-blue-400' },
  swing: { cmd: '/swing', icon: Clock, tf: '4H - Daily', color: 'text-purple-400' },
  position: { cmd: '/swing', icon: Clock, tf: '4H - Daily', color: 'text-purple-400' },
};

const QuizModal = ({ onClose }) => {
  // Flow: intro -> quiz -> submitting -> result (straight to Telegram CTA)
  const [step, setStep] = useState('intro');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [quizResult, setQuizResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchQuestions = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const response = await axios.get(`${API}/quiz/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      setFetchError(true);
      toast.error('Gagal memuat pertanyaan quiz');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const totalSteps = questions.length + 1;
  const progress = (() => {
    if (step === 'intro') return 0;
    if (step === 'quiz') return ((1 + currentQuestion) / totalSteps) * 100;
    return 100;
  })();

  const handleSelectAnswer = useCallback((questionId, answerId) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleSubmitQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, questions.length]);

  const handleBack = useCallback(() => {
    if (step === 'quiz' && currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (step === 'quiz' && currentQuestion === 0) {
      setStep('intro');
    }
  }, [step, currentQuestion]);

  const handleSubmitQuiz = async () => {
    setStep('submitting');

    const formattedAnswers = Object.entries(answers).map(([qId, aId]) => ({
      question_id: parseInt(qId),
      answer_id: aId,
    }));

    try {
      const response = await axios.post(`${API}/quiz/submit`, {
        name: null,
        email: null,
        answers: formattedAnswers,
      });

      setQuizResult(response.data);
      setTimeout(() => setStep('result'), 1500);
    } catch (error) {
      toast.error('Gagal mengirim quiz. Silakan coba lagi.');
      setStep('quiz');
    }
  };

  const handleRetakeQuiz = () => {
    setStep('intro');
    setCurrentQuestion(0);
    setAnswers({});
    setQuizResult(null);
  };

  const handleCopyLink = () => {
    const url = quizResult?.id ? getTelegramDeepLink(quizResult.id) : TELEGRAM_CTA_URL;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link Telegram disalin');
    setTimeout(() => setCopied(false), 2000);
  };

  const currentQ = questions[currentQuestion];

  const stepLabel = (() => {
    if (step === 'intro') return 'Quiz Trading';
    if (step === 'quiz') return `Pertanyaan ${currentQuestion + 1} dari ${questions.length}`;
    if (step === 'submitting') return 'Memproses...';
    if (step === 'result') return 'Hasil Anda';
    return '';
  })();

  const renderResultContent = () => {
    const traderType = quizResult.trader_type;
    const traderInfo = quizResult.trader_info;
    const stratInfo = STRATEGY_COMMANDS[traderType] || STRATEGY_COMMANDS.swing;
    const StratIcon = stratInfo.icon;

    return (
      <>
        <div className="w-20 h-20 rounded-2xl bg-tc-secondary/10 flex items-center justify-center mx-auto mb-6">
          <StratIcon className={`w-10 h-10 ${stratInfo.color}`} />
        </div>

        <p className="text-sm text-tc-primary font-mono uppercase tracking-wider mb-2">
          Gaya Trading Anda
        </p>
        <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
          {traderInfo.name}
        </h2>
        <p className="text-lg text-tc-text-secondary mb-8 max-w-lg mx-auto">
          {traderInfo.description}
        </p>

        {/* Bot command card */}
        <div className="glass-card rounded-2xl p-6 mb-8 max-w-md mx-auto text-left">
          <p className="text-sm text-tc-text-secondary mb-3">Perintah bot AI Anda di Telegram:</p>
          <div className="flex items-center justify-between bg-tc-surface rounded-xl p-4 border border-tc-border">
            <div>
              <span className={`font-mono text-2xl font-bold ${stratInfo.color}`}>
                {stratInfo.cmd}
              </span>
              <p className="text-xs text-tc-text-secondary mt-1">Timeframe: {stratInfo.tf}</p>
            </div>
            <StratIcon className={`w-8 h-8 ${stratInfo.color} opacity-50`} />
          </div>
          {traderType === 'position' && (
            <p className="text-xs text-tc-text-secondary mt-3">
              Sebagai position trader, gunakan /swing sebagai strategi bot terdekat.
            </p>
          )}
        </div>

        {/* Direct Telegram CTA */}
        <div className="space-y-4">
          {/* Onboard code box */}
          <div className="glass-card rounded-2xl p-6 max-w-md mx-auto border border-tc-primary/30">
            <p className="text-sm text-tc-text-secondary mb-3">Ketik ini di bot Telegram setelah bergabung:</p>
            <div className="flex items-center justify-center gap-3 bg-tc-surface-highlight/50 rounded-xl px-5 py-4 border border-tc-border">
              <code className="text-lg font-mono text-tc-primary font-bold select-all">
                /onboard {quizResult.id}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`/onboard ${quizResult.id}`);
                  setCopiedCode(true);
                  toast.success('Perintah disalin');
                  setTimeout(() => setCopiedCode(false), 2000);
                }}
                className="p-2 rounded-lg hover:bg-tc-surface-highlight transition-colors"
                data-testid="copy-onboard-code"
              >
                {copiedCode ? <Check className="w-4 h-4 text-tc-secondary" /> : <Copy className="w-4 h-4 text-tc-text-secondary hover:text-white" />}
              </button>
            </div>
            <p className="text-xs text-tc-text-secondary mt-3">
              Bot AI akan mengenali tipe trader Anda dan merekomendasikan {stratInfo.cmd} untuk analisis chart.
            </p>
          </div>

          <Button
            onClick={() => window.open(quizResult?.id ? getTelegramDeepLink(quizResult.id) : TELEGRAM_CTA_URL, '_blank', 'noopener,noreferrer')}
            className="bg-tc-primary hover:bg-tc-primary-hover text-white px-10 py-7 text-lg font-bold tracking-wider rounded-full btn-glow animate-pulse-glow"
            data-testid="result-telegram-btn"
          >
            <Send className="w-5 h-5 mr-3" />
            GABUNG TELEGRAM SEKARANG
          </Button>

          <div>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 text-sm text-tc-text-secondary hover:text-white transition-colors"
              data-testid="result-copy-link"
            >
              {copied ? <Check className="w-4 h-4 text-tc-secondary" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Link disalin' : 'Salin link Telegram'}
            </button>
          </div>

          <button
            onClick={handleRetakeQuiz}
            className="text-xs text-tc-text-secondary hover:text-white transition-colors mt-2 block mx-auto"
            data-testid="retake-quiz-btn"
          >
            Ulangi quiz dari awal
          </button>
        </div>
      </>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-tc-bg/95 backdrop-blur-md flex flex-col"
      data-testid="quiz-modal"
    >
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-tc-surface-highlight z-50">
        <motion.div
          className="h-full progress-gradient"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-tc-border">
        <div className="flex items-center gap-4">
          {step === 'quiz' && (
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full bg-tc-surface hover:bg-tc-surface-highlight flex items-center justify-center transition-colors"
              data-testid="quiz-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-tc-text-secondary" />
            </button>
          )}
          <span className="text-tc-text-secondary text-sm font-medium">
            {stepLabel}
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-tc-surface hover:bg-tc-surface-highlight flex items-center justify-center transition-colors"
          data-testid="quiz-close-btn"
        >
          <X className="w-5 h-5 text-tc-text-secondary" />
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <Loader2 className="w-12 h-12 text-tc-primary animate-spin mx-auto mb-4" />
                <p className="text-tc-text-secondary">Memuat...</p>
              </motion.div>

            ) : fetchError ? (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                  <X className="w-10 h-10 text-red-400" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-white mb-4">
                  Gagal memuat quiz
                </h3>
                <p className="text-tc-text-secondary mb-8">
                  Periksa koneksi internet Anda dan coba lagi.
                </p>
                <Button
                  onClick={fetchQuestions}
                  className="bg-tc-primary hover:bg-tc-primary-hover text-white px-8 py-6 text-lg font-bold rounded-full"
                  data-testid="quiz-retry-btn"
                >
                  COBA LAGI
                </Button>
              </motion.div>

            ) : step === 'intro' ? (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-2xl bg-tc-primary/10 flex items-center justify-center mx-auto mb-8">
                  <svg className="w-10 h-10 text-tc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                  Temukan gaya trading Anda
                </h2>
                <p className="text-lg text-tc-text-secondary mb-4 max-w-lg mx-auto">
                  Jawab {questions.length} pertanyaan singkat tentang cara Anda menghadapi situasi trading.
                  Tidak ada jawaban benar atau salah.
                </p>
                <p className="text-sm text-tc-text-secondary mb-10">
                  Setelah selesai, Anda akan tahu strategi mana yang paling cocok dan langsung bisa akses bot AI.
                </p>
                <Button
                  onClick={() => setStep('quiz')}
                  className="bg-tc-primary hover:bg-tc-primary-hover text-white px-10 py-6 text-lg font-bold tracking-wide rounded-full btn-glow"
                  data-testid="quiz-begin-btn"
                >
                  MULAI QUIZ
                  <ArrowRight className="w-5 h-5 ml-3" />
                </Button>
              </motion.div>

            ) : step === 'quiz' && currentQ ? (
              <motion.div
                key={`question-${currentQ.id}`}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8">
                  <span className="text-tc-primary font-mono text-sm tracking-wider uppercase mb-4 block">
                    {currentQ.category.replaceAll('_', ' ')}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-heading font-semibold text-white leading-tight">
                    {currentQ.question}
                  </h3>
                </div>

                <div className="space-y-4">
                  {currentQ.options.map((option, idx) => {
                    const isSelected = answers[currentQ.id] === option.id;
                    return (
                      <motion.button
                        key={option.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.1 }}
                        onClick={() => handleSelectAnswer(currentQ.id, option.id)}
                        className={`w-full text-left p-6 rounded-xl border transition-all duration-200 quiz-option ${
                          isSelected
                            ? 'selected border-tc-primary bg-tc-primary/15'
                            : 'border-tc-border bg-tc-surface/30 hover:border-tc-primary/50'
                        }`}
                        data-testid={`quiz-option-${option.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'border-tc-primary bg-tc-primary' : 'border-tc-border'
                          }`}>
                            {isSelected && <CheckCircle className="w-5 h-5 text-white" />}
                          </div>
                          <span className={`text-lg ${isSelected ? 'text-white' : 'text-tc-text-secondary'}`}>
                            {option.text}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Back / Next navigation */}
                <div className="flex items-center justify-between mt-8">
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="border-tc-border text-tc-text-secondary hover:text-white hover:bg-tc-surface-highlight rounded-full px-6 py-5"
                    data-testid="quiz-prev-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Kembali
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={answers[currentQ.id] === undefined}
                    className="bg-tc-primary hover:bg-tc-primary-hover text-white rounded-full px-8 py-5 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="quiz-next-btn"
                  >
                    {currentQuestion < questions.length - 1 ? (
                      <>
                        Lanjut
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Lihat Hasil
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>

            ) : step === 'submitting' ? (
              <motion.div
                key="submitting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="relative w-24 h-24 mx-auto mb-8">
                  <div className="absolute inset-0 rounded-full border-4 border-tc-surface-highlight" />
                  <div className="absolute inset-0 rounded-full border-4 border-tc-primary border-t-transparent animate-spin" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-white mb-4">
                  Sedang menganalisis jawaban Anda...
                </h3>
                <p className="text-tc-text-secondary">
                  Menentukan gaya trading yang paling cocok untuk Anda
                </p>
              </motion.div>

            ) : step === 'result' && quizResult ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                {renderResultContent()}
              </motion.div>

            ) : null}
          </AnimatePresence>
        </div>
      </main>
    </motion.div>
  );
};

export default QuizModal;
